import { Injectable, computed, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { OnboardingChecklist } from '../models/onboarding-checklist.model';
import { OnboardingChecklistApiService } from '../services/onboarding-checklist-api.service';

const VISIBLE_FLAGS = [
  'firstProjectCreated',
  'firstProcessCreated',
  'firstCardCreated',
  'firstCardMoved',
  'firstCommentAdded',
  'firstAutomationCreated',
] as const satisfies ReadonlyArray<keyof OnboardingChecklist>;

const VISIBLE_TOTAL = VISIBLE_FLAGS.length;

function countVisible(checklist: OnboardingChecklist | null): number {
  if (!checklist) return 0;
  return VISIBLE_FLAGS.reduce((acc, key) => acc + (checklist[key] ? 1 : 0), 0);
}

@Injectable({ providedIn: 'root' })
export class OnboardingChecklistStore {
  private readonly checklistSignal = signal<OnboardingChecklist | null>(null);
  private readonly celebrationTriggerSignal = signal(false);
  private celebrationShownThisSession = false;

  readonly checklist = this.checklistSignal.asReadonly();
  readonly celebrationTrigger = this.celebrationTriggerSignal.asReadonly();
  readonly total = VISIBLE_TOTAL;

  readonly visible = computed(() => {
    const c = this.checklistSignal();
    return !!c && !c.isCompleted;
  });
  readonly completedCount = computed(() => countVisible(this.checklistSignal()));
  readonly allDone = computed(() => this.completedCount() === VISIBLE_TOTAL);

  constructor(private readonly api: OnboardingChecklistApiService) {}

  resetSession(): void {
    this.checklistSignal.set(null);
    this.celebrationTriggerSignal.set(false);
    this.celebrationShownThisSession = false;
  }

  async refresh(): Promise<void> {
    try {
      const next = await firstValueFrom(this.api.getChecklist());
      const previous = this.checklistSignal();
      const previousCount = countVisible(previous);
      const nextCount = countVisible(next);
      const wasIncompleteBelowTotal =
        !!previous && !previous.isCompleted && previousCount < VISIBLE_TOTAL;
      const reachedTotalOpen = nextCount === VISIBLE_TOTAL && !next.isCompleted;

      this.checklistSignal.set(next);

      const isTransition = wasIncompleteBelowTotal && reachedTotalOpen;
      const isFirstLoadAtTotal = !previous && reachedTotalOpen && !this.celebrationShownThisSession;
      if (isTransition || isFirstLoadAtTotal) {
        this.celebrationTriggerSignal.set(true);
        this.celebrationShownThisSession = true;
      }
    } catch {
      this.checklistSignal.set(null);
    }
  }

  closeCelebration(): void {
    this.celebrationTriggerSignal.set(false);
  }

  async dismiss(): Promise<void> {
    this.celebrationTriggerSignal.set(false);
    const updated = await firstValueFrom(this.api.dismiss());
    this.checklistSignal.set(updated);
  }
}
